import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import axios from "axios";

import { getCurrentNotificationToken } from "../../firebase";
import { login } from "../../Model/Login";
import { storeUser } from "../../Model/LocalStorage";
import Logo from "../../images/resources/dddd.png";

const LoginView = ({ handleClose }) => {
  const [ipaddress, setIpaddress] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState("");
  const [errorOpen, setErrorOpen] = useState(false);

  const [values, setValues] = useState({
    username: "",
    password: "",
  });

  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (token) {
      navigate("/home", { replace: true });
      return;
    }

    const referrer = document.referrer;
    const currentOrigin = window.location.origin;

    const blacklistedPages = [
      "/login",
      "/signup",
      "/verify-account",
      "/forgot/password",
    ];

    const isBlacklisted = blacklistedPages.some((path) =>
      referrer.includes(path)
    );

    if (
      referrer &&
      referrer.startsWith(currentOrigin) &&
      !isBlacklisted
    ) {
      localStorage.setItem("backUrl", referrer);
    } else {
      localStorage.setItem(
        "backUrl",
        `${currentOrigin}/home`
      );
    }
  }, [navigate]);

  useEffect(() => {
    axios
      .get("https://api64.ipify.org?format=json")
      .then((res) => {
        setIpaddress(res.data?.ip || "0.0.0.0");
      })
      .catch(() => {
        setIpaddress("0.0.0.0");
      });
  }, []);

  useEffect(() => {
    const reason = new URLSearchParams(
      window.location.search
    ).get("reason");

    if (reason === "logged_out") {
      setMessage(
        "Your account was logged in on another device."
      );
      setErrorOpen(true);
    }
  }, []);

  const handleChange = (e) => {
    setValues((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

const handleSubmit = async (e) => {
  e.preventDefault();

  const email = values.username.trim().toLowerCase();

  setLoading(true);

  try {
    console.log("LOGIN: Getting FCM token...");

    const fcmToken =
      await getCurrentNotificationToken();

    console.log(
      "LOGIN: FCM TOKEN:",
      fcmToken
    );

    const formData = {
      email: email,
      password: values.password,
      ip: ipaddress,
      fcm_token: fcmToken || "",
    };

    console.log(
      "LOGIN FORM DATA:",
      formData
    );

    const result = await login(formData);

    console.log(
      "LOGIN RESPONSE:",
      result
    );

    if (result.sucess) {
      const userInfo = {
        email: result.details.email,
        firstname: result.details.firstname,
        lastname: result.details.lastname,
        phone: result.details.phone,
        id: result.details.id,
        group: result.details.group,
        drowssap: values.password,
      };

      localStorage.setItem(
        "token",
        result.token
      );

      storeUser(userInfo);

      if (!handleClose) {
        const queryParams =
          new URLSearchParams(
            window.location.search
          );

        const redirectUrl =
          queryParams.get("redirect");

        const backPage =
          localStorage.getItem("backUrl");

        if (redirectUrl) {
          window.location.href =
            decodeURIComponent(redirectUrl);
        } else if (
          backPage &&
          !backPage.includes("/login")
        ) {
          window.location.href = backPage;
        } else {
          window.location.href = "/home";
        }
      } else {
        window.location.reload();
        handleClose(false);
      }
    } else {
      setMessage(
        result.errors ||
          result.msg ||
          "Login failed. Please try again."
      );

      setErrorOpen(true);
    }
  } catch (error) {
    console.error(
      "Login error:",
      error
    );

    setMessage(
      "Something went wrong. Please try again."
    );

    setErrorOpen(true);
  } finally {
    setLoading(false);
  }
};

  const isLoginDisabled =
    !values.username.trim() ||
    !values.password.trim();

  return (
    <Box
      sx={{
        minHeight: "90vh",
        backgroundImage:
          "url('/static/bgLogin.jpg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor:
            "rgb(204 202 202 / 45%)",
        },
      }}
    >
      <Paper
        elevation={6}
        sx={{
          position: "relative",
          zIndex: 2,
          width: "100%",
          maxWidth: 500,
          p: 5,
          textAlign: "center",
          backdropFilter: "blur(6px)",
          backgroundColor:
            "rgba(255,255,255,0.95)",
        }}
      >
        <Box sx={{ mb: 2 }}>
          <img
            src={Logo}
            alt="Logo"
            style={{
              width: 250,
              marginBottom: 10,
            }}
          />
        </Box>

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Username"
            variant="outlined"
            name="username"
            value={values.username}
            onChange={handleChange}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ mb: 2 }}
            autoFocus
          />

          <TextField
            fullWidth
            label="Password"
            variant="outlined"
            name="password"
            type={
              showPassword
                ? "text"
                : "password"
            }
            value={values.password}
            onChange={handleChange}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ mb: 1 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() =>
                      setShowPassword(
                        (prev) => !prev
                      )
                    }
                    edge="end"
                  >
                    {showPassword ? (
                      <VisibilityOff />
                    ) : (
                      <Visibility />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box
            sx={{
              textAlign: "right",
              mb: 2,
            }}
          >
            <Link
              to="/forgot/password"
              style={{
                textDecoration: "none",
                color: "#3e4396",
                fontWeight: 500,
                fontSize: 14,
              }}
            >
              Forgot password?
            </Link>
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={
              isLoginDisabled || loading
            }
            sx={{
              height: 48,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              background:
                "linear-gradient(90deg, #495057 0%, #212529 100%)",
              color: "#fff",
            }}
          >
            {loading ? (
              <CircularProgress
                size={24}
                color="inherit"
              />
            ) : (
              "Login"
            )}
          </Button>

          <Typography
            variant="body2"
            sx={{ mt: 2 }}
          >
            Don't have an account?{" "}
            <Link
              to="/signup"
              style={{
                color: "#1876d2",
                fontWeight: 500,
              }}
            >
              Create one
            </Link>
          </Typography>
        </form>
      </Paper>

      <Snackbar
        open={errorOpen}
        autoHideDuration={3000}
        onClose={() => setErrorOpen(false)}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Alert
          severity="error"
          icon={<PriorityHighIcon />}
        >
          {message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LoginView;